import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
} from "reactstrap";
import { useGetRoles, useDeleteRole } from "api/roles/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";

const Roles = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const navigate = useNavigate();
  const { data: rolesData, isLoading } = useGetRoles();
  const deleteRoleMutation = useDeleteRole();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const isAdminRole = useCallback((role) => (role?.name || "").trim().toLowerCase() === "admin", []);

  const handleDeleteClick = useCallback((id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteRoleMutation.mutate(selectedDeleteId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteId(null);
        }
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const roleRows = useMemo(
    () =>
      (rolesData?.results || []).map((role, idx) => ({
        id: role.documentId,
        documentId: role.documentId,
        row_num: idx + 1,
        name: role.name,
        description: role.description,
        role,
      })),
    [rolesData?.results]
  );

  const roleColumns = useMemo(
    () => [
      { Header: "#", accessor: "row_num" },
      { Header: t("roles.name"), accessor: "name" },
      { Header: t("roles.description"), accessor: "description" },
      {
        id: "actions",
        Header: "",
        accessor: "documentId",
        disableSortBy: true,
        Cell: ({ row }) => {
          const r = row.original.role;
          const isProtectedRole = isAdminRole(r);
          return (
            <div className="actions-right" style={{ textAlign: "right" }}>
              {account.permissions.roles_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/roles/edit/${r.documentId}`);
                  }}
                  disabled={isProtectedRole}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {account.permissions.roles_delete && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(r.documentId);
                  }}
                  disabled={isProtectedRole || deleteRoleMutation.isPending}
                >
                  {deleteRoleMutation.isPending && selectedDeleteId === r.documentId ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="tim-icons icon-simple-remove" />
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      account.permissions.roles_delete,
      account.permissions.roles_edit,
      navigate,
      handleDeleteClick,
      deleteRoleMutation.isPending,
      selectedDeleteId,
      isAdminRole,
    ]
  );

  const RolesTable = shellThemeVariant === "2" ? ReactTable2 : ReactTable;

  return (
    <div
      className={classNames("content", "search-roles", {
        "theme2-admin-content-compact": shellThemeVariant === "2",
        "theme2-roles-inset": shellThemeVariant === "2",
      })}
    >
      <style>
        {`
          .search-roles-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .search-roles-card-header h2,
          .search-roles-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .search-roles .align-items-center {
            margin-top: 3px;
          }
          .search-roles .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
      </style>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="search-roles-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-0">{t('roles.title')}</h2>
                {account.permissions.roles_add && (
                  <Button
                    size="sm"
                    style={{ ...activeColorStyle, color: 'white' }}
                    onClick={() => navigate('/roles/add')}
                  >
                    {t('roles.addRole')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div
                className="inventory-table-wrap"
                style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
              >
                <RolesTable
                  activeColor={activeColor}
                  data={roleRows}
                  columns={roleColumns}
                  className="-striped -highlight sticky-stock-table"
                  manualPagination={false}
                  pageSize={20}
                  loading={isLoading}
                  loadingText={t("roles.loading")}
                  noDataText={t("roles.noRolesFound")}
                  onRowClick={(row) => {
                    if (row?.documentId && !isAdminRole(row?.role)) navigate(`/roles/edit/${row.documentId}`);
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('roles.deleteRole')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('roles.deleteConfirmation')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteRoleMutation.isPending}
          >
            {t('roles.cancel')}
          </Button>
          <Button
             style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
          }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteRoleMutation.isPending}
          >
            {deleteRoleMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                {t('roles.deleting')}
              </>
            ) : (
              t('roles.yesDelete')
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Roles;
